import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { superAdminAPI } from '../../services/api';

const AdminsList = () => {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    role: 'support',
    permissions: []
  });
  const [formError, setFormError] = useState('');

  const availablePermissions = [
    { id: 'manage_organizations', label: 'Manage Organizations' },
    { id: 'manage_plans', label: 'Manage Plans' },
    { id: 'manage_subscriptions', label: 'Manage Subscriptions' },
    { id: 'view_analytics', label: 'View Analytics' },
    { id: 'manage_users', label: 'Manage Users' },
    { id: 'view_reports', label: 'View Reports' }
  ];

  const roleLabels = {
    super_admin: 'Super Admin',
    support: 'Support',
    billing: 'Billing'
  };

  const { data: adminsData, isLoading } = useQuery({
    queryKey: ['superadmin-admins'],
    queryFn: superAdminAPI.getAdmins
  });

  const admins = adminsData?.data?.data || [];

  const createMutation = useMutation({
    mutationFn: superAdminAPI.createAdmin,
    onSuccess: () => {
      queryClient.invalidateQueries(['superadmin-admins']);
      closeModal();
    },
    onError: (error) => setFormError(error.response?.data?.message || 'Failed to create admin')
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => superAdminAPI.updateAdmin(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['superadmin-admins']);
      closeModal();
    },
    onError: (error) => setFormError(error.response?.data?.message || 'Failed to update admin')
  });

  const deleteMutation = useMutation({
    mutationFn: superAdminAPI.deleteAdmin,
    onSuccess: () => queryClient.invalidateQueries(['superadmin-admins'])
  });

  const closeModal = () => {
    setShowModal(false);
    setEditingAdmin(null);
    setFormError('');
    setFormData({
      name: '', email: '', password: '', phone: '', role: 'support', permissions: []
    });
  };

  const openEditModal = (admin) => {
    setEditingAdmin(admin);
    setFormData({
      name: admin.name,
      email: admin.email,
      password: '',
      phone: admin.phone || '',
      role: admin.role,
      permissions: admin.permissions || []
    });
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;

    if (name === 'permissions') {
      setFormData(prev => ({
        ...prev,
        permissions: checked
          ? [...prev.permissions, value]
          : prev.permissions.filter(p => p !== value)
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError('');

    if (!formData.name || !formData.email || (!editingAdmin && !formData.password)) {
      setFormError('Please fill in all required fields');
      return;
    }

    const submitData = { ...formData };
    if (editingAdmin && !submitData.password) {
      delete submitData.password;
    }

    if (editingAdmin) {
      updateMutation.mutate({ id: editingAdmin._id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleDelete = (adminId, adminName) => {
    if (window.confirm(`Are you sure you want to delete "${adminName}"? This action cannot be undone.`)) {
      deleteMutation.mutate(adminId);
    }
  };

  const getStatusBadge = (status) => {
    return status === 'active' ? 'badge-success' : 'badge-danger';
  };

  return (
    <div className="admins-page">
      <div className="page-header">
        <div className="header-left">
          <h1>Admin Users</h1>
          <p>Manage platform administrators</p>
        </div>
        <div className="header-right">
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            + Add Admin
          </button>
        </div>
      </div>

      <div className="table-container">
        {isLoading ? (
          <div className="loading">Loading admins...</div>
        ) : admins.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">👥</div>
            <h3>No admin users found</h3>
            <p>Add your first platform administrator</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Phone</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {admins.map((admin) => (
                  <tr key={admin._id}>
                    <td>
                      <div className="admin-name">
                        <span className="name">{admin.name}</span>
                      </div>
                    </td>
                    <td>{admin.email}</td>
                    <td>
                      <span className="badge badge-info">
                        {roleLabels[admin.role] || admin.role}
                      </span>
                    </td>
                    <td>{admin.phone || '-'}</td>
                    <td>
                      <span className={`badge ${getStatusBadge(admin.status)}`}>
                        {admin.status}
                      </span>
                    </td>
                    <td>{new Date(admin.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn btn-sm btn-outline" onClick={() => openEditModal(admin)}>
                          Edit
                        </button>
                        {admin.role !== 'super_admin' && (
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(admin._id, admin.name)}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingAdmin ? 'Edit Admin' : 'Add New Admin'}</h2>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">
              {formError && <div className="error-message">{formError}</div>}

              <div className="form-row">
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="John Doe"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="admin@example.com"
                    required
                    disabled={!!editingAdmin}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{editingAdmin ? 'New Password (leave blank to keep)' : 'Password *'}</label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Minimum 8 characters"
                    required={!editingAdmin}
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+1 234 567 8900"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Role *</label>
                <select name="role" value={formData.role} onChange={handleInputChange} required>
                  <option value="super_admin">Super Admin (Full Access)</option>
                  <option value="support">Support (Limited Access)</option>
                  <option value="billing">Billing (Billing Only)</option>
                </select>
              </div>

              {formData.role !== 'super_admin' && (
                <div className="form-section">
                  <h4>Permissions</h4>
                  <div className="features-grid">
                    {availablePermissions.map(perm => (
                      <label key={perm.id} className="feature-checkbox">
                        <input
                          type="checkbox"
                          name="permissions"
                          value={perm.id}
                          checked={formData.permissions.includes(perm.id)}
                          onChange={handleInputChange}
                        />
                        {perm.label}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={closeModal}>Cancel</button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Saving...'
                    : editingAdmin ? 'Update Admin' : 'Create Admin'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .admins-page {
          padding: 0;
        }
        .admin-name .name {
          font-weight: 500;
        }
        .features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 0.5rem;
        }
        .feature-checkbox {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          font-size: 0.875rem;
        }
        .feature-checkbox input {
          width: 1rem;
          height: 1rem;
        }
      `}</style>
    </div>
  );
};

export default AdminsList;